import { useState, useEffect, useMemo } from 'react';
import PeriodFilter, { usePeriodFilter } from '../components/PeriodFilter';
import { EMPTY_PERIOD } from '../components/common/ClearFiltersButton';
import MultiSelectFilter, { listValue, listParam } from '../components/common/MultiSelectFilter';
import FilterChips from '../components/common/FilterChips';
import SavedViews from '../components/common/SavedViews';
import VoidArInvoiceButton from '../components/VoidArInvoiceButton';
import { useNavigate, useLocation } from 'react-router-dom';
import DocumentWallet from '../components/DocumentWallet';
import AuditLog from '../components/AuditLog';
import EntityLink from '../components/EntityLink';
import NoteText, { InvoiceNotes } from '../components/NoteText';
import api, { downloadCsv, exportIdsParam } from '../utils/api';
import { localDateStr, addDaysStr } from '../utils/dates';
import { useAuth } from '../context/AuthContext';
import { useUrlParams } from '../hooks/useUrlState';
import Pagination from '../components/Pagination';
import Badge from '../components/Badge';
import StateBlock from '../components/StateBlock';
import CsvImportModal from '../components/CsvImportModal';
import HexPaymentImportModal from '../components/HexPaymentImportModal';
import SortTh, { useSort } from '../components/common/SortableHeader';
import { useConfirm, useToast } from '../context/FeedbackContext';
import ConsultingInvoiceModal from '../components/ConsultingInvoiceModal';
import ConsultingLines from '../components/ConsultingLines';

const INVOICE_TYPE_LABELS = { standard: 'Standard', passthrough: 'Allowance / Reimbursement', fte_referral: 'FTE Referral', credit_note: 'Credit Note', consulting: 'Business Consulting' };
const PAYMENT_METHOD_LABELS = { ach: 'ACH', check: 'Check', wire: 'Wire' };

// Aging / collection state, computed server-side (routes/arInvoices.js
// agingOf) with the same bucket edges as the Aging Report.
const AGING_STATES = [
  { value: 'not_due', label: 'Not due yet', variant: 'info' },
  { value: 'overdue_1_30', label: '1-30 days overdue', variant: 'warning' },
  { value: 'overdue_31_60', label: '31-60 days overdue', variant: 'warning' },
  { value: 'overdue_61_90', label: '61-90 days overdue', variant: 'danger' },
  { value: 'overdue_90_plus', label: '90+ days overdue', variant: 'danger' },
  { value: 'paid', label: 'Paid', variant: 'success' },
  { value: 'draft', label: 'Draft', variant: undefined },
  { value: 'credit', label: 'Credit note', variant: undefined },
  { value: 'void', label: 'Void', variant: undefined },
];
const AGING_BY_VALUE = Object.fromEntries(AGING_STATES.map(a => [a.value, a]));

const AR_STATUSES = [
  { value: 'draft', label: 'Draft' },
  { value: 'sent', label: 'Sent' },
  { value: 'partial', label: 'Partial' },
  { value: 'paid', label: 'Paid' },
  { value: 'void', label: 'Void' },
];

// Is there a vendor (AP) invoice for this AR? Same PO + type + period match
// as the dashboard's "AP received - AR not raised" check, reversed.
const AP_RECEIVED_STATES = [
  { value: 'received', label: 'Vendor invoice received' },
  { value: 'not_received', label: 'No vendor invoice yet' },
  { value: 'no_po', label: 'No PO on AR invoice' },
  { value: 'not_applicable', label: 'Not applicable (Consulting / Referral / FTE - no vendor)' },
];

export default function ARInvoicesPage() {
  const toast = useToast();
  const { user } = useAuth();
  const location = useLocation();
  const [invoices, setInvoices] = useState(null);
  const [error, setError] = useState(false);
  const { period, setPeriod, filterByPeriod } = usePeriodFilter("period_start");
  const { get, set } = useUrlParams();
  // Multi-select filters: OR within a field, AND across fields (same as AP
  // Invoices). Old single-value links (?status=all) still work.
  const agingFilter = listValue(get('aging', ''));
  const statusFilter = listValue(get('status', '')).filter(v => v !== 'all');
  const typeFilter = listValue(get('type', '')).filter(v => v !== 'all');
  const vendorFilter = listValue(get('vendor', ''));
  const contractorFilter = listValue(get('contractor', ''));
  const apFilter = listValue(get('ap', ''));
  const search = get('search', '');
  const page = get('page', 1);
  const pageSize = get('pageSize', 25);
  const [showImport, setShowImport] = useState(false);
  const [showHexImport, setShowHexImport] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [detailId, setDetailId] = useState(null);
  const { sortKey, sortDir, handleSort, sortData } = useSort('invoice_date', 'desc');
  const [workOrders, setWorkOrders] = useState([]);
  const [pos, setPos] = useState([]);
  const [form, setForm] = useState({ invoice_number: '', wo_id: '', po_id: '', invoice_date: '', period_start: '', period_end: '', hours: '', rate: '', amount: '', due_date: '', notes: '', invoice_type: 'standard', offer_number: '', employee_id: '', allowance_type: '', allowance_category: '', reference_note: '' });
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState('');

  function load() {
    setError(false);
    api.get('/ar-invoices').then(r => setInvoices(r.data)).catch(() => setError(true));
  }

  useEffect(() => {
    load();
    api.get('/work-orders').then(r => setWorkOrders(r.data));
    api.get('/purchase-orders').then(r => setPos(r.data));
  }, []);
  useEffect(() => {
    if (location.state?.po_id && pos.length > 0) {
      const po = pos.find(p => p.id === location.state.po_id);
      if (po) {
        selectWO(po.wo_id);
        selectPO(po.id);
        setShowForm(true);
      }
    }
  }, [location.state, pos]);

  // Split invoices (e.g. TMV/JUN/2026-53 / TMV/JUN/2026-53A: same base
  // number, PO and period) are one bill in two rows - badge them and keep
  // them together in results.
  const splitKeyOf = i => `${String(i.invoice_number || '').trim().replace(/(\d)A$/i, '$1')}|${i.po_id || ''}|${i.period_start || ''}`;
  const splitGroups = useMemo(() => {
    const groups = new Map();
    for (const i of invoices || []) {
      const k = splitKeyOf(i);
      groups.set(k, (groups.get(k) || 0) + 1);
    }
    return groups;
  }, [invoices]);
  const isSplit = i => (splitGroups.get(splitKeyOf(i)) || 0) > 1;

  const filtered = useMemo(() => {
    if (!invoices) return [];
    const s = search.toLowerCase();
    const matches = i =>
      (!agingFilter.length || agingFilter.includes(i.aging)) &&
      (!statusFilter.length || statusFilter.includes(i.status)) &&
      (!typeFilter.length || typeFilter.includes(i.invoice_type || 'standard')) &&
      (!vendorFilter.length || vendorFilter.includes(String(i.vendor_id))) &&
      (!contractorFilter.length || contractorFilter.includes(String(i.contractor_id))) &&
      (!apFilter.length || apFilter.includes(i.ap_received)) &&
      (!s || i.invoice_number.toLowerCase().includes(s) ||
        (i.client_name || '').toLowerCase().includes(s) ||
        (i.contractor_name || '').toLowerCase().includes(s) ||
        (i.vendor_name || '').toLowerCase().includes(s) ||
        (i.po_number || '').toLowerCase().includes(s) ||
        (i.client_wo_number || '').toLowerCase().includes(s));
    const direct = new Set(filterByPeriod(invoices.filter(matches)).map(i => i.id));
    // Pull in the other half of any matched split pair.
    const matchedSplitKeys = new Set(invoices.filter(i => direct.has(i.id) && isSplit(i)).map(splitKeyOf));
    return invoices.filter(i => direct.has(i.id) || (matchedSplitKeys.size && matchedSplitKeys.has(splitKeyOf(i)) && isSplit(i)));
  }, [invoices, agingFilter.join(), statusFilter.join(), typeFilter.join(), vendorFilter.join(), contractorFilter.join(), apFilter.join(), search, period, filterByPeriod, splitGroups]);

  // Dropdown options (with counts over the whole list).
  const countBy = (keyFn) => {
    const m = new Map();
    for (const i of invoices || []) { const k = keyFn(i); if (k != null) m.set(k, (m.get(k) || 0) + 1); }
    return m;
  };
  const filterOptions = useMemo(() => {
    const agingCounts = countBy(i => i.aging);
    const statusCounts = countBy(i => i.status);
    const typeCounts = countBy(i => i.invoice_type || 'standard');
    const apCounts = countBy(i => i.ap_received);
    const vendorNames = new Map((invoices || []).filter(i => i.vendor_id).map(i => [String(i.vendor_id), i.vendor_name || '—']));
    const contractorNames = new Map((invoices || []).filter(i => i.contractor_id).map(i => [String(i.contractor_id), i.contractor_name || '—']));
    const vendorCounts = countBy(i => i.vendor_id ? String(i.vendor_id) : null);
    const contractorCounts = countBy(i => i.contractor_id ? String(i.contractor_id) : null);
    const byName = (a, b) => a.label.localeCompare(b.label);
    return {
      aging: AGING_STATES.filter(a => agingCounts.get(a.value)).map(a => ({ value: a.value, label: a.label, count: agingCounts.get(a.value) })),
      status: AR_STATUSES.map(s => ({ ...s, count: statusCounts.get(s.value) || 0 })),
      type: Object.entries(INVOICE_TYPE_LABELS).map(([value, label]) => ({ value, label, count: typeCounts.get(value) || 0 })),
      vendor: [...vendorNames].map(([value, label]) => ({ value, label, count: vendorCounts.get(value) })).sort(byName),
      contractor: [...contractorNames].map(([value, label]) => ({ value, label, count: contractorCounts.get(value) })).sort(byName),
      ap: AP_RECEIVED_STATES.map(a => ({ ...a, count: apCounts.get(a.value) || 0 })),
    };
  }, [invoices]);

  const optionLabel = (field, value) => (filterOptions[field].find(o => o.value === value) || {}).label || value;
  const setList = (key, values) => set({ [key]: listParam(values), page: 1 });
  const FIELD_NAMES = { aging: 'Aging', status: 'Status', type: 'Type', vendor: 'Vendor', contractor: 'Contractor', ap: 'Vendor invoice' };
  const currentLists = { aging: agingFilter, status: statusFilter, type: typeFilter, vendor: vendorFilter, contractor: contractorFilter, ap: apFilter };
  const chips = [
    ...Object.entries(currentLists).flatMap(([field, values]) => values.map(v => ({
      key: `${field}:${v}`,
      label: `${FIELD_NAMES[field]}: ${optionLabel(field, v)}`,
      onRemove: () => setList(field, values.filter(x => x !== v)),
    }))),
    ...(search ? [{ key: 'search', label: `Search: "${search}"`, onRemove: () => set({ search: '', page: 1 }) }] : []),
    ...(period.from || period.to ? [{ key: 'period', label: `Period: ${period.from || '…'} → ${period.to || '…'}`, onRemove: () => setPeriod(EMPTY_PERIOD) }] : []),
  ];
  const clearAllFilters = () => { set({ aging: '', status: '', type: '', vendor: '', contractor: '', ap: '', search: '', page: 1 }); setPeriod(EMPTY_PERIOD); };
  const savedViewState = { aging: agingFilter, status: statusFilter, type: typeFilter, vendor: vendorFilter, contractor: contractorFilter, ap: apFilter, search, period };
  const applySavedView = v => {
    set({
      aging: listParam(v.aging || []), status: listParam(v.status || []), type: listParam(v.type || []),
      vendor: listParam(v.vendor || []), contractor: listParam(v.contractor || []), ap: listParam(v.ap || []),
      search: v.search || '', page: 1,
    });
    setPeriod(v.period || EMPTY_PERIOD);
  };
  const outstandingTotal = filtered.reduce((sum, i) => sum + Math.max(0, (i.amount || 0) - (i.verified_paid || 0)) * (['void', 'draft', 'credit'].includes(i.aging) ? 0 : 1), 0);

  const sorted = useMemo(() => {
    return sortData(filtered, {
      invoice_number: (i) => i.invoice_number || "",
      client_name: (i) => i.client_name || "",
      contractor_name: (i) => i.contractor_name || "",
      invoice_date: (i) => i.invoice_date || "",
      period_start: (i) => i.period_start || "",
      period_end: (i) => i.period_end || "",
      amount: (i) => i.amount || 0,
      verified_paid: (i) => i.verified_paid || 0,
      status: (i) => i.status || "",
      aging: (i) => AGING_STATES.findIndex(a => a.value === i.aging),
    });
  }, [filtered, sortKey, sortDir]);

  const paged = useMemo(() => {
    const start = (page - 1) * pageSize;
    return sorted.slice(start, start + pageSize);
  }, [sorted, page, pageSize]);

  function selectWO(woId) {
    const wo = workOrders.find(w => w.id === Number(woId));
    setForm(f => ({
      ...f, wo_id: woId, rate: wo?.sell_rate || f.rate, po_id: "",
      period_start: wo?.engagement_type === 'fte_placement' ? (wo.start_date || f.period_start) : f.period_start,
    }));
  }

  function selectPO(poId) {
    const po = pos.find(p => p.id === Number(poId));
    setForm(f => {
      const hours = po?.approved_hours || po?.timesheet_hours || f.hours;
      const rate = po?.sell_rate || f.rate;
      const canCalc = hours && !isNaN(Number(hours)) && rate && !isNaN(Number(rate));
      return {
        ...f, po_id: poId,
        wo_id: po?.wo_id ? String(po.wo_id) : f.wo_id,
        rate,
        hours,
        amount: canCalc ? String(Number(hours) * Number(rate)) : f.amount,
        period_start: po?.period_start || f.period_start,
        period_end: po?.period_end || f.period_end,
      };
    });
  }

  async function createInvoice() {
    if (!form.wo_id || !form.invoice_date) {
      setFormError('Required fields missing');
      return;
    }
    setSaving(true);
    setFormError('');
    try {
      const payload = form.invoice_type === 'fte_referral'
        ? { ...form, hours: null, rate: form.amount }
        : form;
      const res = await api.post('/ar-invoices', payload);
      setForm({ invoice_number: '', wo_id: '', po_id: '', invoice_date: '', period_start: '', period_end: '', hours: '', rate: '', amount: '', due_date: '', notes: '', invoice_type: 'standard', offer_number: '', employee_id: '', allowance_type: '', allowance_category: '', reference_note: '' });
      setShowForm(false);
      load();
      if (res?.data?.id) {
        downloadCsv(`/ar-invoices/${res.data.id}/pdf`, `${res.data.invoice_number || 'invoice'}.pdf`).catch(() => {});
      }
    } catch (e) {
      setFormError(e.response?.data?.error || 'Create failed');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="page">
      <div className="page-header">
        <h1 className="page-title">AR Invoices</h1>
        <div style={{ display: 'flex', gap: 10 }}>
          <button className="btn btn-secondary" onClick={() => {
            downloadCsv('/ar-invoices/export', 'ar-invoices.csv', exportIdsParam(filtered, invoices))
              .catch(() => toast('Could not export CSV', 'error'));
          }}>↓ Export CSV</button>
          <button className="btn btn-secondary" onClick={() => setShowImport(true)}>↑ Upload CSV</button>
          <button className="btn btn-secondary" onClick={() => setShowHexImport(true)}>↑ Import Hex Payment Report</button>
          <button className="btn btn-primary" onClick={() => {
            const invoice_date = localDateStr();
            const due_date = addDaysStr(invoice_date, 45);
            setForm(f => ({ ...f, invoice_date, due_date }));
            setShowForm(true);
          }}>+ Create Invoice</button>
        </div>
      </div>

      {showImport && <CsvImportModal type="ar-invoices" onClose={() => setShowImport(false)} onImported={load} />}
      {showHexImport && <HexPaymentImportModal onClose={() => setShowHexImport(false)} onImported={load} />}

      {showForm && (
        <div className="ar-invoice-form" style={{ background: 'var(--surface)', padding: 16, borderRadius: 'var(--radius)', marginBottom: 20 }}>
          <h3 style={{ marginBottom: 16 }}>Create AR Invoice</h3>
          {formError && <div style={{ color: 'var(--error)', marginBottom: 10, fontSize: 12 }}>{formError}</div>}

          <div style={{ fontSize: 13, color: 'var(--muted)', marginBottom: 8 }}>1. Invoice Type</div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8, marginBottom: 20 }}>
            {[
              { v: 'standard', label: 'Standard', sub: 'Hourly billing' },
              { v: 'passthrough', label: 'Allowance / Reimbursement', sub: 'Expenses and allowances' },
              { v: 'fte_referral', label: 'FTE Referral', sub: 'One-time placement fee' },
            ].map(t => (
              <button key={t.v} type="button" onClick={() => setForm(f => ({
                ...f, invoice_type: t.v, wo_id: '', po_id: '', hours: '', rate: '',
                period_start: '', period_end: '', offer_number: '', employee_id: '',
                allowance_type: '', allowance_category: '', reference_note: '',
              }))} style={{
                padding: '10px 12px', textAlign: 'left', borderRadius: 'var(--radius)', cursor: 'pointer',
                border: form.invoice_type === t.v ? '2px solid var(--primary)' : '1px solid var(--border)',
                background: form.invoice_type === t.v ? 'var(--info-bg)' : 'var(--card)',
              }}>
                <div style={{ fontSize: 13, fontWeight: 600 }}>{t.label}</div>
                <div style={{ fontSize: 11, color: 'var(--muted)' }}>{t.sub}</div>
              </button>
            ))}
          </div>

          <div style={{ fontSize: 13, color: 'var(--muted)', marginBottom: 8 }}>2. Invoice Information</div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12, marginBottom: 20 }}>
            <div style={{ gridColumn: 'span 3' }}>
              <label style={{ fontSize: 12, fontWeight: 600, marginBottom: 4, display: 'block' }}>Invoice # (auto-generated if left blank)</label>
              <input type="text" placeholder="e.g. TMV/JUL/2026-01 - leave blank to auto-generate" value={form.invoice_number} onChange={e => setForm(f => ({ ...f, invoice_number: e.target.value }))} />
            </div>
            <div>
              <label style={{ fontSize: 12, fontWeight: 600, marginBottom: 4, display: 'block' }}>Invoice Date</label>
              <input type="date" value={form.invoice_date} onChange={e => setForm(f => {
                const invoice_date = e.target.value;
                let due_date = f.due_date;
                if (invoice_date) {
                  due_date = addDaysStr(invoice_date, 45);
                }
                return { ...f, invoice_date, due_date };
              })} />
            </div>
            <div>
              <label style={{ fontSize: 12, fontWeight: 600, marginBottom: 4, display: 'block' }}>Work Order</label>
              <select value={form.wo_id} onChange={e => selectWO(e.target.value)}>
                <option value="">Select Work Order</option>
                {workOrders.filter(w => (form.invoice_type === 'fte_referral' ? w.engagement_type === 'fte_placement' : w.engagement_type === 'contract_placement')).sort((a, b) => a.contractor_name.localeCompare(b.contractor_name)).map(w => (
                  <option key={w.id} value={w.id}>{w.contractor_name} - {w.outgoing_wo_number} ({w.client_wo_number}){w.status !== 'active' ? ' (closed)' : ''}</option>
                ))}
              </select>
            </div>
            <div>
              <label style={{ fontSize: 12, fontWeight: 600, marginBottom: 4, display: 'block' }}>Purchase Order</label>
              <select value={form.po_id} onChange={e => selectPO(e.target.value)}>
                <option value="">Select PO (optional)</option>
                {pos.filter(p => p.wo_id === Number(form.wo_id)).map(p => (
                  <option key={p.id} value={p.id}>{p.po_number}</option>
                ))}
              </select>
            </div>
          </div>

          <div style={{ fontSize: 13, color: 'var(--muted)', marginBottom: 8 }}>
            3. {form.invoice_type === 'fte_referral' ? 'Placement Details' : form.invoice_type === 'passthrough' ? 'Expense Details' : 'Billing Period and Hours'}
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12, marginBottom: 20 }}>
            {form.invoice_type === 'fte_referral' && (
              <div>
                <label style={{ fontSize: 12, fontWeight: 600, marginBottom: 4, display: 'block' }}>Date of Joining</label>
                <input type="date" value={form.period_start} onChange={e => setForm(f => ({ ...f, period_start: e.target.value }))} />
              </div>
            )}
            {form.invoice_type === 'standard' && (
              <>
                <div>
                  <label style={{ fontSize: 12, fontWeight: 600, marginBottom: 4, display: 'block' }}>Period Start</label>
                  <input type="date" value={form.period_start} onChange={e => setForm(f => ({ ...f, period_start: e.target.value }))} />
                </div>
                <div>
                  <label style={{ fontSize: 12, fontWeight: 600, marginBottom: 4, display: 'block' }}>Period End</label>
                  <input type="date" value={form.period_end} onChange={e => setForm(f => ({ ...f, period_end: e.target.value }))} />
                </div>
                <div></div>
              </>
            )}
            {form.invoice_type === 'fte_referral' && (
              <>
                <div>
                  <label style={{ fontSize: 12, fontWeight: 600, marginBottom: 4, display: 'block' }}>Offer Number</label>
                  <input type="text" value={form.offer_number} onChange={e => setForm(f => ({ ...f, offer_number: e.target.value }))} />
                </div>
                <div>
                  <label style={{ fontSize: 12, fontWeight: 600, marginBottom: 4, display: 'block' }}>Employee ID</label>
                  <input type="text" value={form.employee_id} onChange={e => setForm(f => ({ ...f, employee_id: e.target.value }))} />
                </div>
                <div>
                  <label style={{ fontSize: 12, fontWeight: 600, marginBottom: 4, display: 'block' }}>Amount</label>
                  <input type="number" step="0.01" placeholder="e.g. 5000" value={form.amount} onChange={e => setForm(f => ({ ...f, amount: e.target.value }))} />
                </div>
              </>
            )}
            {form.invoice_type === 'standard' && (
              <>
                <div>
                  <label style={{ fontSize: 12, fontWeight: 600, marginBottom: 4, display: 'block' }}>Hours</label>
                  <input type="number" step="0.01" placeholder="e.g. 176" value={form.hours} onChange={e => setForm(f => {
                    const hours = e.target.value;
                    const rate = Number(f.rate);
                    const canCalc = hours !== '' && !isNaN(Number(hours)) && !isNaN(rate) && rate > 0;
                    return { ...f, hours, amount: canCalc ? String(Number(hours) * rate) : f.amount };
                  })} />
                </div>
                <div>
                  <label style={{ fontSize: 12, fontWeight: 600, marginBottom: 4, display: 'block' }}>Rate ($/hr)</label>
                  <input type="number" step="0.01" placeholder="e.g. 75" value={form.rate} onChange={e => setForm(f => {
                    const rate = e.target.value;
                    const hours = Number(f.hours);
                    const canCalc = rate !== '' && !isNaN(Number(rate)) && !isNaN(hours) && hours > 0;
                    return { ...f, rate, amount: canCalc ? String(hours * Number(rate)) : f.amount };
                  })} />
                </div>
                <div>
                  <label style={{ fontSize: 12, fontWeight: 600, marginBottom: 4, display: 'block' }}>Amount</label>
                  <input type="number" step="0.01" placeholder="Auto-fills from hours × rate" value={form.amount} onChange={e => setForm(f => ({ ...f, amount: e.target.value }))} />
                </div>
              </>
            )}
            {form.invoice_type === 'passthrough' && (
              <>
                <div>
                  <label style={{ fontSize: 12, fontWeight: 600, marginBottom: 4, display: 'block' }}>Type</label>
                  <select value={form.allowance_type} onChange={e => setForm(f => ({ ...f, allowance_type: e.target.value }))}>
                    <option value="">Select type</option>
                    <option value="allowance">Allowance</option>
                    <option value="reimbursement">Reimbursement</option>
                  </select>
                </div>
                <div>
                  <label style={{ fontSize: 12, fontWeight: 600, marginBottom: 4, display: 'block' }}>Category</label>
                  <input type="text" placeholder="e.g. Shift Allowance, Phone, Taxi" value={form.allowance_category} onChange={e => setForm(f => ({ ...f, allowance_category: e.target.value }))} />
                </div>
                <div></div>
                <div>
                  <label style={{ fontSize: 12, fontWeight: 600, marginBottom: 4, display: 'block' }}>Period Start</label>
                  <input type="date" value={form.period_start} onChange={e => setForm(f => ({ ...f, period_start: e.target.value }))} />
                </div>
                <div>
                  <label style={{ fontSize: 12, fontWeight: 600, marginBottom: 4, display: 'block' }}>Period End</label>
                  <input type="date" value={form.period_end} onChange={e => setForm(f => ({ ...f, period_end: e.target.value }))} />
                </div>
                <div></div>
                <div>
                  <label style={{ fontSize: 12, fontWeight: 600, marginBottom: 4, display: 'block' }}>Reference Note (appears on invoice, e.g. "Manager email attached")</label>
                  <input type="text" placeholder="e.g. Manager email attached, Phone bill attached" value={form.reference_note} onChange={e => setForm(f => ({ ...f, reference_note: e.target.value }))} />
                </div>
                <div>
                  <label style={{ fontSize: 12, fontWeight: 600, marginBottom: 4, display: 'block' }}>Amount</label>
                  <input type="number" step="0.01" placeholder="e.g. 500" value={form.amount} onChange={e => setForm(f => ({ ...f, amount: e.target.value }))} />
                </div>
                <div></div>
              </>
            )}
          </div>

          <div style={{ fontSize: 13, color: 'var(--muted)', marginBottom: 8 }}>4. Due Date and Notes</div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: 12, marginBottom: 20 }}>
            <div>
              <label style={{ fontSize: 12, fontWeight: 600, marginBottom: 4, display: 'block' }}>Due Date</label>
              <input type="date" value={form.due_date} onChange={e => setForm(f => ({ ...f, due_date: e.target.value }))} />
            </div>
            <div>
              <label style={{ fontSize: 12, fontWeight: 600, marginBottom: 4, display: 'block' }}>Notes</label>
              <textarea value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} rows="2" />
            </div>
          </div>

          <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
            <button className="btn btn-secondary" onClick={() => setShowForm(false)}>Cancel</button>
            <button className="btn btn-primary" onClick={createInvoice} disabled={saving}>{saving ? 'Creating…' : 'Create'}</button>
          </div>
        </div>
      )}

      <div className="filter-bar">
        <PeriodFilter value={period} onChange={setPeriod} />
        <input type="text" className="filter-search" placeholder="Search invoice #, contractor, vendor, WO # or PO #…" value={search} onChange={e => set({ search: e.target.value, page: 1 })} />
        <MultiSelectFilter label="Aging" options={filterOptions.aging} selected={agingFilter} onChange={v => setList('aging', v)} />
        <MultiSelectFilter label="Status" options={filterOptions.status} selected={statusFilter} onChange={v => setList('status', v)} />
        <MultiSelectFilter label="Type" options={filterOptions.type} selected={typeFilter} onChange={v => setList('type', v)} />
        <MultiSelectFilter label="Vendor" options={filterOptions.vendor} selected={vendorFilter} onChange={v => setList('vendor', v)} />
        <MultiSelectFilter label="Contractor" options={filterOptions.contractor} selected={contractorFilter} onChange={v => setList('contractor', v)} />
        <MultiSelectFilter label="Vendor invoice" options={filterOptions.ap} selected={apFilter} onChange={v => setList('ap', v)} />
        <SavedViews pageKey="ar_invoices" currentState={savedViewState} canSave={chips.length > 0} onApply={applySavedView} />
      </div>
      <FilterChips chips={chips} onClearAll={clearAllFilters} resultCount={filtered.length} totalCount={invoices?.length || 0} />
      {chips.length > 0 && filtered.length > 0 && (
        <div style={{ fontSize: 12, color: 'var(--muted)', margin: '-8px 0 12px' }}>
          Outstanding in these results: <strong style={{ color: 'var(--text)' }}>${outstandingTotal.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</strong>
        </div>
      )}

      {error && <StateBlock type="error" action={<button className="btn btn-primary" onClick={load}>Try again</button>} />}
      {!error && invoices === null && <StateBlock type="loading" />}
      {!error && invoices !== null && filtered.length === 0 && <StateBlock type="noResults" />}
      {!error && filtered.length > 0 && (
        <>
          <table className="data-table">
            <thead>
              <tr>
                <SortTh label="Invoice #" sortKey="invoice_number" currentKey={sortKey} currentDir={sortDir} onSort={handleSort} /><SortTh label="Client" sortKey="client_name" currentKey={sortKey} currentDir={sortDir} onSort={handleSort} /><SortTh label="Contractor" sortKey="contractor_name" currentKey={sortKey} currentDir={sortDir} onSort={handleSort} /><SortTh label="Period Start" sortKey="period_start" currentKey={sortKey} currentDir={sortDir} onSort={handleSort} /><SortTh label="Period End" sortKey="period_end" currentKey={sortKey} currentDir={sortDir} onSort={handleSort} /><th title="Type">Type</th><SortTh label="Amount" sortKey="amount" currentKey={sortKey} currentDir={sortDir} onSort={handleSort} align="right" /><SortTh label="Verified Paid" sortKey="verified_paid" currentKey={sortKey} currentDir={sortDir} onSort={handleSort} align="right" /><SortTh label="Status" sortKey="status" currentKey={sortKey} currentDir={sortDir} onSort={handleSort} /><SortTh label="Aging" sortKey="aging" currentKey={sortKey} currentDir={sortDir} onSort={handleSort} />
              </tr>
            </thead>
            <tbody>
              {paged.map(inv => (
                <tr key={inv.id}>
                  <td>
                    <button className="row-name-link" onClick={() => setDetailId(inv.id)}>{inv.invoice_number}</button>
                    {isSplit(inv) && <span className="split-badge" title="Split invoice - one bill across two rows (same base number, PO and period)">SPLIT</span>}
                  </td>
                  <td><EntityLink type="client" id={inv.client_id}>{inv.client_name}</EntityLink></td>
                  <td>{inv.contractor_id ? <EntityLink type="contractor" id={inv.contractor_id}>{inv.contractor_name}</EntityLink> : <span style={{ color: 'var(--muted)' }}>{inv.invoice_type === 'consulting' ? 'Business Consulting' : '—'}</span>}</td>
                  <td>{inv.period_start || "-"}</td><td>{inv.period_end || "-"}</td>
                  <td><Badge variant={inv.invoice_type === 'passthrough' ? 'info' : inv.invoice_type === 'fte_referral' ? 'warning' : inv.invoice_type === 'credit_note' ? 'danger' : inv.invoice_type === 'consulting' ? 'success' : undefined}>{inv.invoice_type === "passthrough" ? "Exp" : inv.invoice_type === "fte_referral" ? "FTE" : inv.invoice_type === "credit_note" ? "CN" : inv.invoice_type === "consulting" ? "Consulting" : "Std"}</Badge></td>
                  <td>${inv.amount.toLocaleString(undefined, { minimumFractionDigits: 2 })}</td>
                  <td>${(inv.verified_paid || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}</td>
                  <td><Badge>{inv.status}</Badge></td>
                  <td>
                    {inv.aging && (
                      <Badge variant={AGING_BY_VALUE[inv.aging]?.variant}>{AGING_BY_VALUE[inv.aging]?.label || inv.aging}</Badge>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <Pagination page={page} pageSize={pageSize} total={filtered.length} onPageChange={(p) => set({ page: p })} onPageSizeChange={(s) => set({ pageSize: s, page: 1 })} />
        </>
      )}

      {detailId && <ARInvoiceDetailPanel invoiceId={detailId} onClose={() => setDetailId(null)} onChanged={load} />}
    </div>
  );
}

function ARInvoiceDetailPanel({ invoiceId, onClose, onChanged }) {
  const confirm = useConfirm();
  const toast = useToast();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editForm, setEditForm] = useState(null);
  const [editSaving, setEditSaving] = useState(false);
  const [editError, setEditError] = useState('');
  const [showPayForm, setShowPayForm] = useState(false);
  const [editingPaymentId, setEditingPaymentId] = useState(null);
  const EMPTY_PAY_FORM = { payment_date: '', cash_amount: '', epd_rate: '', epd_amount: '', bgc_amount: '', reference: '', batch_ref: '', notes: '', payment_method: '' };
  const [showConsultingEdit, setShowConsultingEdit] = useState(false);
  const isConsulting = data?.invoice_type === 'consulting';
  const [payForm, setPayForm] = useState(EMPTY_PAY_FORM);
  const [saving, setSaving] = useState(false);

  function load() {
    api.get(`/ar-invoices/${invoiceId}`).then(r => setData(r.data)).catch(e => { console.error("Failed to load invoice:", e); setData(null); });
  }

  useEffect(load, [invoiceId]);

  async function savePayment() {
    setSaving(true);
    try {
      const payload = {
        ...payForm,
        cash_amount: Number(payForm.cash_amount || 0),
        payment_method: payForm.payment_method || null,
        epd_rate: payForm.epd_rate ? Number(payForm.epd_rate) : null,
        epd_amount: payForm.epd_amount ? Number(payForm.epd_amount) : 0,
        bgc_amount: payForm.bgc_amount ? Number(payForm.bgc_amount) : 0,
      };
      if (editingPaymentId) {
        await api.put(`/ar-invoices/${invoiceId}/payments/${editingPaymentId}`, payload);
      } else {
        await api.post(`/ar-invoices/${invoiceId}/payments`, payload);
      }
      setPayForm(EMPTY_PAY_FORM);
      setEditingPaymentId(null);
      setShowPayForm(false);
      load();
      onChanged();
    } finally { setSaving(false); }
  }
  function startEditPayment(p) {
    setPayForm({
      payment_date: p.payment_date || '',
      cash_amount: p.cash_amount ?? '',
      epd_rate: p.epd_rate ?? '',
      epd_amount: p.epd_amount ?? '',
      bgc_amount: p.bgc_amount ?? '',
      reference: p.reference || '',
      batch_ref: p.batch_ref || '',
      notes: p.notes || '',
      payment_method: p.payment_method || '',
    });
    setEditingPaymentId(p.id);
    setShowPayForm(true);
  }

  async function setVerification(paymentId, status) {
    await api.put(`/ar-invoices/${invoiceId}/payments/${paymentId}/verify`, { verification_status: status });
    load();
    onChanged();
  }

  function startEdit() {
    setEditForm({ ...data });
    setIsEditing(true);
    setEditError('');
  }

  async function saveEdit() {
    if (!editForm.invoice_number?.trim()) {
      setEditError('Invoice number required');
      return;
    }
    setEditSaving(true);
    setEditError('');
    try {
      await api.put(`/ar-invoices/${invoiceId}`, editForm);
      setIsEditing(false);
      setEditForm(null);
      load();
      onChanged();
      toast('Invoice updated', 'success');
    } catch (e) {
      setEditError('Save failed: ' + (e.response?.data?.error || e.message));
    } finally {
      setEditSaving(false);
    }
  }

  async function markSubmitted() {
    setSaving(true);
    try {
      await api.post(`/ar-invoices/${invoiceId}/submit`);
      load();
      onChanged();
    } catch (e) {
      toast('Failed: ' + (e.response?.data?.error || e.message), 'error');
    } finally {
      setSaving(false);
    }
  }

  async function revertToDraft() {
    if (!(await confirm({ title: 'Revert to Draft?', message: 'This invoice goes back to Draft. Only allowed if it has no payments recorded.', confirmLabel: 'Revert to Draft' }))) return;
    setSaving(true);
    try {
      await api.post(`/ar-invoices/${invoiceId}/revert-to-draft`);
      load();
      onChanged();
    } catch (e) {
      toast('Failed: ' + (e.response?.data?.error || e.message), 'error');
    } finally {
      setSaving(false);
    }
  }

  function cancelEdit() {
    setIsEditing(false);
    setEditForm(null);
    setEditError('');
  }

  return (
    <>
      <div className="panel-overlay" onClick={onClose} />
      <div className="quick-panel" style={{ width: 500 }}>
        <div className="drawer-header">
          <h3 className="drawer-title">
            {isEditing ? 'Edit Invoice' : (data?.invoice_number || 'Loading…')}
          </h3>
          <div style={{ display: 'flex', gap: 8 }}>
            {!isEditing && user?.role === 'admin' && (
              <button className="btn btn-sm btn-secondary" onClick={() => (isConsulting ? setShowConsultingEdit(true) : startEdit())}>
                ✏️ Edit
              </button>
            )}
            {!isEditing && data?.status === 'draft' && (
              <button className="btn btn-sm btn-primary" onClick={markSubmitted} disabled={saving}>
                {saving ? 'Submitting…' : '✓ Mark as Submitted'}
              </button>
            )}
            {!isEditing && data?.status === 'sent' && (
              <button className="btn btn-sm btn-secondary" onClick={revertToDraft} disabled={saving}>
                {saving ? 'Reverting…' : '↩ Revert to Draft'}
              </button>
            )}
            {!isEditing && user?.role === 'admin' && data && (
              <VoidArInvoiceButton invoice={{ ...data, id: invoiceId }} onVoided={() => { load(); onChanged(); }} />
            )}
            <button className="btn btn-sm btn-secondary" onClick={() => downloadCsv(`/ar-invoices/${invoiceId}/pdf`, `${data?.invoice_number || 'invoice'}.pdf`).catch(e => toast('Could not generate PDF: ' + (e.response?.data?.error || e.message), 'error'))}>
              📄 PDF
            </button>
            <button className="btn btn-sm btn-secondary" onClick={() => navigate(`/ar-invoices/${invoiceId}`)}>
              View Full Page ↗
            </button>
            <button className="drawer-close" onClick={onClose}>×</button>
          </div>
        </div>

        {data && !isEditing && (
          <div className="panel-body">
            <div className="panel-summary-line">
              <Badge>{data.status}</Badge>
              <Badge variant={data.invoice_type === 'passthrough' ? 'info' : data.invoice_type === 'fte_referral' ? 'warning' : data.invoice_type === 'credit_note' ? 'danger' : isConsulting ? 'success' : undefined}>
                {data.invoice_type === 'passthrough' ? 'Allowance / Reimbursement' : data.invoice_type === 'fte_referral' ? 'FTE Referral (one-time fee)' : data.invoice_type === 'credit_note' ? 'Credit Note' : isConsulting ? 'Business Consulting' : 'Standard'}
              </Badge>
              {data.client_name}{data.contractor_name ? ` · ${data.contractor_name}` : ''}
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, fontSize: 13, marginBottom: 16 }}>
              <DetailRow label="Invoice #" value={data.invoice_number} />
              <DetailRow label="Client" value={data.client_name} link={data.client_id ? { type: 'client', id: data.client_id } : null} />
              <DetailRow label="Contractor" value={data.contractor_name} link={data.contractor_id ? { type: 'contractor', id: data.contractor_id } : null} />
              <DetailRow label="Work Order" value={data.client_wo_number} link={data.wo_id ? { type: 'work_order', id: data.wo_id } : null} />
              <DetailRow label="Client PO" value={data.po_number} link={data.po_id ? { type: 'purchase_order', id: data.po_id } : null} />
              <DetailRow label="Invoice Date" value={data.invoice_date} />
              <DetailRow label="Due Date" value={data.due_date} />
              <DetailRow label="Period" value={data.period_start && data.period_end ? `${data.period_start} – ${data.period_end}` : null} />
              {!isConsulting && <DetailRow label="Hours" value={data.hours} />}
              {!isConsulting && <DetailRow label="Rate" value={data.rate != null ? `$${data.rate}/hr` : null} />}
            </div>

            {isConsulting && <ConsultingLines lines={data.lines || []} invoice={data} />}

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 20 }}>
              <div style={{ background: 'var(--surface)', borderRadius: 'var(--radius)', padding: 12 }}>
                <div style={{ fontSize: 12, color: 'var(--muted)' }}>Invoice Amount</div>
                <div style={{ fontSize: 16, fontWeight: 700 }}>
                  ${data.amount.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                </div>
              </div>
              <div style={{ background: 'var(--surface)', borderRadius: 'var(--radius)', padding: 12 }}>
                <div style={{ fontSize: 12, color: 'var(--muted)' }}>Outstanding</div>
                <div style={{ fontSize: 16, fontWeight: 700 }}>
                  ${data.summary.outstanding.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                </div>
              </div>
            </div>

            <InvoiceNotes notes={data.notes} />

            <h4 style={{ fontSize: 13, fontWeight: 700, marginBottom: 10 }}>Payments</h4>
            {data.payments.length === 0 && (
              <div className="form-hint" style={{ marginBottom: 12 }}>No payments recorded yet.</div>
            )}
            {data.payments.map(p => (
              <div key={p.id} style={{ background: 'var(--surface)', borderRadius: 'var(--radius)', padding: 10, marginBottom: 8, fontSize: 12 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
                  <strong>${p.cash_amount.toLocaleString(undefined, { minimumFractionDigits: 2 })}</strong>
                  <Badge variant={p.verification_status === 'verified' ? 'success' : 'warning'}>
                    {p.verification_status}
                  </Badge>
                </div>
                {(p.epd_amount > 0 || p.bgc_amount > 0) && (
                  <div style={{ display: 'flex', gap: 12, fontSize: 11, marginBottom: 4 }}>
                    {p.epd_amount > 0 && (
                      <span style={{ color: 'var(--muted)' }}>
                        EPD{p.epd_rate ? ` (${(p.epd_rate * 100).toFixed(0)}%)` : ''}: <strong style={{ color: 'var(--warning)' }}>-${Number(p.epd_amount).toLocaleString(undefined, { minimumFractionDigits: 2 })}</strong>
                      </span>
                    )}
                    {p.bgc_amount > 0 && (
                      <span style={{ color: 'var(--muted)' }}>
                        BGC: <strong style={{ color: 'var(--warning)' }}>-${Number(p.bgc_amount).toLocaleString(undefined, { minimumFractionDigits: 2 })}</strong>
                      </span>
                    )}
                  </div>
                )}
                <div style={{ color: 'var(--muted)', fontSize: 11 }}>
                  {p.payment_date}{p.payment_method ? ` · ${PAYMENT_METHOD_LABELS[p.payment_method] || p.payment_method}` : ''}{p.reference ? ` · ${p.reference}` : ''}
                </div>
                <NoteText text={p.notes} />
                {user?.role === 'admin' && (
                  <div style={{ display: 'flex', gap: 6, marginTop: 8 }}>
                    <button className="btn btn-ghost btn-sm" onClick={() => setVerification(p.id, 'verified')}>
                      ✓ Verify
                    </button>
                    <button className="btn btn-ghost btn-sm" onClick={() => setVerification(p.id, 'disputed')}>
                      ✗ Dispute
                    </button>
                    <button className="btn btn-ghost btn-sm" onClick={() => startEditPayment(p)}>
                      ✎ Edit
                    </button>
                  </div>
                )}
              </div>
            ))}

            <button
              className="btn btn-secondary"
              style={{ width: '100%', marginTop: 14 }}
              onClick={() => setShowPayForm(true)}
            >
              + Record Payment
            </button>

            {showPayForm && (
              <div style={{ marginTop: 16, padding: 12, background: 'var(--surface)', borderRadius: 'var(--radius)' }}>
                <h5 style={{ marginBottom: 10, fontSize: 12, fontWeight: 700 }}>{editingPaymentId ? 'Edit Payment' : 'Record Payment'}</h5>
                <div className="form-field" style={{ marginBottom: 10 }}>
                  <label>Payment Date</label>
                  <input type="date" value={payForm.payment_date} onChange={e => setPayForm(f => ({ ...f, payment_date: e.target.value }))} />
                </div>
                <div className="form-field" style={{ marginBottom: 10 }}>
                  <label>Cash Amount</label>
                  <input type="number" step="0.01" value={payForm.cash_amount} onChange={e => setPayForm(f => ({ ...f, cash_amount: e.target.value }))} />
                </div>
                {isConsulting && (
                  <div className="form-field" style={{ marginBottom: 10 }}>
                    <label>Method</label>
                    <select value={payForm.payment_method} onChange={e => setPayForm(f => ({ ...f, payment_method: e.target.value }))}>
                      <option value="">Select…</option>
                      <option value="ach">ACH</option>
                      <option value="check">Check</option>
                      <option value="wire">Wire</option>
                    </select>
                    <span className="form-hint">Partial payments are fine. Recorded as unverified until matched to the bank or verified.</span>
                  </div>
                )}
                {!isConsulting && <>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 10 }}>
                  <div className="form-field">
                    <label>EPD Rate (%)</label>
                    <input type="number" step="0.01" value={payForm.epd_rate} onChange={e => setPayForm(f => ({ ...f, epd_rate: e.target.value }))} />
                  </div>
                  <div className="form-field">
                    <label>EPD Amount</label>
                    <input type="number" step="0.01" value={payForm.epd_amount} onChange={e => setPayForm(f => ({ ...f, epd_amount: e.target.value }))} />
                  </div>
                </div>
                <div className="form-field" style={{ marginBottom: 10 }}>
                  <label>BGC Amount (Background Verification cost)</label>
                  <input type="number" step="0.01" value={payForm.bgc_amount} onChange={e => setPayForm(f => ({ ...f, bgc_amount: e.target.value }))} />
                </div>
                </>}
                <div className="form-field" style={{ marginBottom: 10 }}>
                  <label>Reference</label>
                  <input value={payForm.reference} onChange={e => setPayForm(f => ({ ...f, reference: e.target.value }))} />
                </div>
                {!isConsulting && <div className="form-field" style={{ marginBottom: 10 }}>
                  <label>Batch Ref # (Hexaware bulk payment reference)</label>
                  <input value={payForm.batch_ref} onChange={e => setPayForm(f => ({ ...f, batch_ref: e.target.value }))} />
                </div>}
                <div className="form-field" style={{ marginBottom: 10 }}>
                  <label>Notes</label>
                  <input value={payForm.notes} onChange={e => setPayForm(f => ({ ...f, notes: e.target.value }))} />
                </div>
                <div style={{ display: 'flex', gap: 8 }}>
                  <button className="btn btn-secondary" style={{ flex: 1 }} onClick={() => { setShowPayForm(false); setEditingPaymentId(null); setPayForm(EMPTY_PAY_FORM); }}>Cancel</button>
                  <button className="btn btn-primary" style={{ flex: 1 }} onClick={savePayment} disabled={saving}>{saving ? 'Saving…' : 'Save'}</button>
                </div>
              </div>
            )}
            <DocumentWallet entityType="ar_invoice" entityId={invoiceId} entityName={data?.invoice_number} />
            <AuditLog tableName="ar_invoices" recordId={invoiceId} />
          </div>
        )}

        {editForm && isEditing && (
          <div className="panel-body">
            {editError && (
              <div style={{ background: '#fee', color: '#c33', padding: 10, borderRadius: 4, marginBottom: 12, fontSize: 12 }}>
                {editError}
              </div>
            )}

            <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: 10 }}>
              <div className="form-field">
                <label>Invoice #</label>
                <input type="text" value={editForm.invoice_number || ''} onChange={e => setEditForm(f => ({ ...f, invoice_number: e.target.value }))} />
              </div>

              <div className="form-field">
                <label>Invoice Date</label>
                <input type="date" value={editForm.invoice_date || ''} onChange={e => setEditForm(f => ({ ...f, invoice_date: e.target.value }))} />
              </div>

              <div className="form-field">
                <label>Period Start</label>
                <input type="date" value={editForm.period_start || ''} onChange={e => setEditForm(f => ({ ...f, period_start: e.target.value }))} />
              </div>

              <div className="form-field">
                <label>Period End</label>
                <input type="date" value={editForm.period_end || ''} onChange={e => setEditForm(f => ({ ...f, period_end: e.target.value }))} />
              </div>

              {editForm.invoice_type !== 'passthrough' && editForm.invoice_type !== 'credit_note' && editForm.invoice_type !== 'fte_referral' && (
                <>
                  <div className="form-field">
                    <label>Hours Billed</label>
                    <input type="number" step="0.01" value={editForm.hours_billed || ''} onChange={e => setEditForm(f => ({ ...f, hours_billed: e.target.value }))} />
                  </div>
                  <div className="form-field">
                    <label>Hours Approved</label>
                    <input type="number" step="0.01" value={editForm.hours || ''} onChange={e => setEditForm(f => ({ ...f, hours: e.target.value }))} />
                  </div>

                  <div className="form-field">
                    <label>Rate ($/hr)</label>
                    <input type="number" step="0.01" value={editForm.rate || ''} onChange={e => setEditForm(f => ({ ...f, rate: e.target.value }))} />
                  </div>
                </>
              )}

              <div className="form-field">
                <label>Amount</label>
                <input type="number" step="0.01" value={editForm.amount || ''} onChange={e => setEditForm(f => ({ ...f, amount: e.target.value }))} />
              </div>

              <div className="form-field">
                <label>Due Date</label>
                <input type="date" value={editForm.due_date || ''} onChange={e => setEditForm(f => ({ ...f, due_date: e.target.value }))} />
              </div>

              <div className="form-field">
                <label>Invoice Type</label>
                <select value={editForm.invoice_type || 'standard'} onChange={e => setEditForm(f => ({ ...f, invoice_type: e.target.value }))}>
                  <option value="standard">Standard</option>
                  <option value="passthrough">Allowance / Reimbursement</option>
                  <option value="fte_referral">FTE Referral (one-time placement fee)</option>
                  <option value="credit_note">Credit Note</option>
                </select>
              </div>

              <div className="form-field">
                <label>Notes</label>
                <textarea value={editForm.notes || ''} onChange={e => setEditForm(f => ({ ...f, notes: e.target.value }))} rows="3" />
              </div>
              {editForm.invoice_type === 'passthrough' && (
                <div className="form-field">
                  <label>Reference Note (prints on invoice)</label>
                  <input type="text" placeholder="e.g. Manager email attached, Phone bill attached" value={editForm.reference_note || ''} onChange={e => setEditForm(f => ({ ...f, reference_note: e.target.value }))} />
                </div>
              )}
            </div>

            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 16 }}>
              <button className="btn btn-secondary" onClick={cancelEdit} disabled={editSaving}>
                Cancel
              </button>
              <button className="btn btn-primary" onClick={saveEdit} disabled={editSaving}>
                {editSaving ? 'Saving…' : 'Save Changes'}
              </button>
            </div>
          </div>
        )}

        {showConsultingEdit && data && (
          <ConsultingInvoiceModal invoice={{ ...data, id: invoiceId }} onClose={() => setShowConsultingEdit(false)}
            onSaved={() => { setShowConsultingEdit(false); load(); onChanged(); toast('Invoice updated', 'success'); }} />
        )}

        {!data && (
          <div className="panel-body">
            <div style={{ textAlign: 'center', padding: 20, color: 'var(--muted)' }}>
              Loading…
            </div>
          </div>
        )}
      </div>
    </>
  );
}

function DetailRow({ label, value, link }) {
  if (value === null || value === undefined || value === '') return null;
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
      <span style={{ color: 'var(--muted)' }}>{label}</span>
      {link ? (
        <EntityLink type={link.type} id={link.id} style={{ fontWeight: 600 }}>{value}</EntityLink>
      ) : (
        <span style={{ fontWeight: 600 }}>{value}</span>
      )}
    </div>
  );
}
